import type { OfflineBehaviorView } from './types';

import { STORAGE_KEYS } from '~/constants/storageKeys';

const OFFLINE_BEHAVIOR_VIEWS_KEY = STORAGE_KEYS.OFFLINE_BEHAVIOR_VIEWS;
const MAX_OFFLINE_BEHAVIOR_VIEWS = 50;

export function getOfflineBehaviorViews(): OfflineBehaviorView[] {
	if (!import.meta.client) return [];
	try {
		const stored = localStorage.getItem(OFFLINE_BEHAVIOR_VIEWS_KEY);
		return stored ? JSON.parse(stored) : [];
	} catch {
		return [];
	}
}

export function saveOfflineBehaviorViews(views: OfflineBehaviorView[]): void {
	if (!import.meta.client) return;
	try {
		localStorage.setItem(
			OFFLINE_BEHAVIOR_VIEWS_KEY,
			JSON.stringify(views.slice(-MAX_OFFLINE_BEHAVIOR_VIEWS))
		);
	} catch {
		// localStorage unavailable
	}
}

export function clearOfflineBehaviorViews(): void {
	if (!import.meta.client) return;
	try {
		localStorage.removeItem(OFFLINE_BEHAVIOR_VIEWS_KEY);
	} catch {
		// localStorage unavailable
	}
}

export function hasBufferedBehaviorViews(): boolean {
	return getOfflineBehaviorViews().length > 0;
}

/**
 * Reads and clears the buffer in one step so two flushers (login watch and
 * lead submit) cannot both send the same view.
 */
export function takeOfflineBehaviorViews(): OfflineBehaviorView[] {
	const views = getOfflineBehaviorViews();
	if (views.length === 0) return [];
	clearOfflineBehaviorViews();
	return views;
}

export function hasOfflineBehaviorViewForProperty(propertyId: string): boolean {
	return getOfflineBehaviorViews().some(view => view.propertyId === propertyId);
}

function replaceOfflineBehaviorViews(remaining: OfflineBehaviorView[]): void {
	if (remaining.length === 0) {
		clearOfflineBehaviorViews();
		return;
	}
	saveOfflineBehaviorViews(remaining);
}

export function removeOfflineBehaviorView(
	propertyId: string,
	viewType: string
): void {
	const views = getOfflineBehaviorViews();
	const remaining = views.filter(
		view => !(view.propertyId === propertyId && view.viewType === viewType)
	);
	if (remaining.length === views.length) return;
	replaceOfflineBehaviorViews(remaining);
}

export function removeOfflineBehaviorViewsForProperty(
	propertyId: string
): void {
	const views = getOfflineBehaviorViews();
	const remaining = views.filter(view => view.propertyId !== propertyId);
	if (remaining.length === views.length) return;
	replaceOfflineBehaviorViews(remaining);
}

export function saveBehaviorViewOffline(view: OfflineBehaviorView): void {
	const views = getOfflineBehaviorViews();
	const existingIndex = views.findIndex(
		v => v.propertyId === view.propertyId && v.viewType === view.viewType
	);

	if (existingIndex >= 0) {
		views[existingIndex] = view;
	} else {
		views.push(view);
	}

	saveOfflineBehaviorViews(views);
}

export function getOfflineBehaviorViewsCount(): number {
	return getOfflineBehaviorViews().length;
}
